import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

// Same logic as Client Portfolio's health calculation, duplicated here on
// purpose since it's a small pure function and this page only needs the
// breakdown counts, not the per-client table itself.
function computeHealth(attainmentPct, hasOverdueInvoice) {
  if (hasOverdueInvoice) return attainmentPct !== null && attainmentPct < 60 ? "red" : "amber";
  if (attainmentPct === null) return "green";
  if (attainmentPct >= 90) return "green";
  if (attainmentPct >= 60) return "amber";
  return "red";
}

function daysElapsedInMonth() {
  return new Date().getDate();
}

export default function CeoDashboard() {
  const [financials, setFinancials] = useState([]);
  const [deals, setDeals] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [metricsThisMonth, setMetricsThisMonth] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffHours, setStaffHours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const thisMonth = new Date().toISOString().slice(0, 7);
    const monthStart = `${thisMonth}-01`;

    const [finRes, dealsRes, clientsRes, projectsRes, invoicesRes, metricsRes, staffRes, hoursRes] = await Promise.all([
      supabase.from("financial_entries").select("*").order("month", { ascending: false }).limit(2),
      supabase.from("deals").select("*"),
      supabase.from("clients").select("*"),
      supabase.from("projects").select("id, client_id, goal_targets"),
      supabase.from("invoices").select("client_id, status"),
      supabase.from("metrics").select("*, projects!inner(client_id)").gte("date", monthStart),
      supabase.from("users").select("id, weekly_capacity_hours").in("role", ["agency_admin", "agency_staff"]),
      supabase.from("staff_hours").select("hours_logged").gte("date", monthStart),
    ]);

    setFinancials(finRes.data || []);
    setDeals(dealsRes.data || []);
    setClients(clientsRes.data || []);
    setProjects(projectsRes.data || []);
    setInvoices(invoicesRes.data || []);
    setMetricsThisMonth(metricsRes.data || []);
    setStaff(staffRes.data || []);
    setStaffHours(hoursRes.data || []);
    setLoading(false);
  }

  const rollup = useMemo(() => {
    const current = financials[0];
    const previous = financials[1];
    const growth = current && previous?.revenue
      ? Math.round(((current.revenue - previous.revenue) / previous.revenue) * 100)
      : null;
    const grossMargin = current?.revenue ? Math.round((current.gross_profit / current.revenue) * 100) : null;

    const openDeals = deals.filter((d) => !["won", "lost"].includes(d.stage));
    const won = deals.filter((d) => d.stage === "won").length;
    const lost = deals.filter((d) => d.stage === "lost").length;
    const winRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : null;
    const pipeline = openDeals.reduce((sum, d) => sum + Number(d.value), 0);

    const thisMonth = new Date().toISOString().slice(0, 7);
    const newClients = clients.filter((c) => c.created_at?.slice(0, 7) === thisMonth).length;
    const activeClients = clients.filter((c) => c.lifecycle_status === "active").length;
    const churnedClients = clients.filter((c) => c.lifecycle_status === "churned").length;
    const churnRate = clients.length > 0 ? Math.round((churnedClients / clients.length) * 100) : 0;

    const teamSize = staff.length;
    const totalCapacity = staff.reduce((sum, s) => sum + (s.weekly_capacity_hours || 40), 0);
    const totalLogged = staffHours.reduce((sum, h) => sum + Number(h.hours_logged), 0);
    const possibleHours = totalCapacity * (daysElapsedInMonth() / 7);
    const utilization = possibleHours > 0 ? Math.round((totalLogged / possibleHours) * 100) : null;
    const revenuePerEmployee = teamSize > 0 && current?.revenue ? Math.round(current.revenue / teamSize) : null;

    const healthCounts = { green: 0, amber: 0, red: 0 };
    clients.forEach((cl) => {
      const clientInvoices = invoices.filter((i) => i.client_id === cl.id);
      const hasOverdue = clientInvoices.some((i) => i.status === "overdue");
      const clientProjects = projects.filter((p) => p.client_id === cl.id);
      const goalPcts = [];
      clientProjects.forEach((p) => {
        Object.entries(p.goal_targets || {}).forEach(([key, target]) => {
          const actual = metricsThisMonth
            .filter((m) => m.project_id === p.id && m.metric_key === key)
            .reduce((sum, m) => sum + Number(m.value), 0);
          if (Number(target) > 0) goalPcts.push((actual / Number(target)) * 100);
        });
      });
      const attainment = goalPcts.length > 0 ? goalPcts.reduce((a, b) => a + b, 0) / goalPcts.length : null;
      healthCounts[computeHealth(attainment, hasOverdue)] += 1;
    });

    return {
      current,
      growth,
      grossMargin,
      pipeline,
      winRate,
      newClients,
      activeClients,
      churnRate,
      teamSize,
      utilization,
      revenuePerEmployee,
      healthCounts,
    };
  }, [financials, deals, clients, projects, invoices, metricsThisMonth, staff, staffHours]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  const c = rollup.current;

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">CEO dashboard</h1>
      <p className="text-sm text-muted-foreground mb-6">
        A live rollup of Financials, Sales &amp; Pipeline, Client Portfolio, and Services
      </p>

      {!c ? (
        <p className="text-sm text-muted-foreground">Log at least one month in Financials to see this rollup.</p>
      ) : (
        <>
          <Section label="From Financials" />
          <div className="grid grid-cols-4 gap-3 mb-5">
            <Kpi label="Revenue" value={`₹${Number(c.revenue).toLocaleString("en-IN")}`} sub={c.revenue_target ? `${Math.round((c.revenue / c.revenue_target) * 100)}% of target` : null} />
            <Kpi label="Target" value={c.revenue_target ? `₹${Number(c.revenue_target).toLocaleString("en-IN")}` : "-"} />
            <Kpi label="Growth" value={rollup.growth !== null ? `${rollup.growth >= 0 ? "+" : ""}${rollup.growth}%` : "-"} sub="vs last month" />
            <Kpi label="Gross margin" value={rollup.grossMargin !== null ? `${rollup.grossMargin}%` : "-"} />
            <Kpi label="Net profit" value={c.net_profit ? `₹${Number(c.net_profit).toLocaleString("en-IN")}` : "-"} />
            <Kpi label="Cash" value={c.cash_balance ? `₹${Number(c.cash_balance).toLocaleString("en-IN")}` : "-"} sub={c.cash_balance && c.operating_expenses ? `${(c.cash_balance / c.operating_expenses).toFixed(1)} months runway` : null} />
          </div>

          <Section label="From Sales & Pipeline" />
          <div className="grid grid-cols-4 gap-3 mb-5">
            <Kpi label="Pipeline" value={`₹${rollup.pipeline.toLocaleString("en-IN")}`} sub={c.revenue_target ? `${Math.round((rollup.pipeline / c.revenue_target) * 100)}% of target` : null} />
            <Kpi label="Win rate" value={rollup.winRate !== null ? `${rollup.winRate}%` : "-"} />
          </div>

          <Section label="From Client Portfolio" />
          <div className="grid grid-cols-4 gap-3 mb-5">
            <Kpi label="New clients" value={rollup.newClients} />
            <Kpi label="Churn" value={`${rollup.churnRate}%`} />
            <Kpi label="Active clients" value={rollup.activeClients} />
          </div>

          <Section label="Team capacity" />
          <div className="grid grid-cols-4 gap-3 mb-5">
            <Kpi label="Team" value={rollup.teamSize} />
            <Kpi label="Utilization" value={rollup.utilization !== null ? `${rollup.utilization}%` : "-"} sub="hours logged / capacity" />
            <Kpi label="Revenue / employee" value={rollup.revenuePerEmployee ? `₹${rollup.revenuePerEmployee.toLocaleString("en-IN")}` : "-"} />
          </div>

          <div className="border border-border rounded-2xl bg-card p-5">
            <div className="text-sm font-semibold text-foreground mb-3">Client health breakdown</div>
            <div className="flex gap-6 items-center">
              <HealthItem color="bg-primary" count={rollup.healthCounts.green} label="good" />
              <HealthItem color="bg-yellow-600" count={rollup.healthCounts.amber} label="watch" />
              <HealthItem color="bg-destructive" count={rollup.healthCounts.red} label="at risk" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ label }) {
  return (
    <div className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-2 mt-1">{label}</div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function HealthItem({ color, count, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={`w-3 h-3 rounded-full ${color}`} />
      <strong className="text-foreground">{count}</strong> {label}
    </div>
  );
}
